/**
 * What the Fleet screen says about each robot (UI round of 2026-11-02):
 * the pure half, read by the table, the phone rows, the summary line and
 * Needs attention alike.
 *
 * Nothing here re-reads a robot. The lines come from its presence
 * (`robot-lines.ts`, the classification the map draws from), and the
 * reasons from the attention list's own copy table (`robot-copy.ts`), so
 * Fleet can never name a robot's trouble differently from the Overview.
 */
import type { RobotStateView, TaskStateView } from '../../api/rmf-client';
import type { PlaceRegistry, RobotCommissionState, ScheduleOccurrence } from '../../api/v1';
import type { Audience } from '../../auth/roles';
import type { AttentionSeverity, SummaryFact } from '../../design-system/components/ScreenParts';
import type { StatusKind } from '../../design-system/tokens';
import {
  BAND_RANK,
  robotCopyForCategory,
  robotCopyForReason,
  robotCopyRow,
  type Band,
  type RobotCopyKey,
  type RobotCopyRow,
} from '../../state/attention/robot-copy';
import { BATTERY_LOW_PCT, BATTERY_VERY_LOW_PCT } from '../../state/battery-levels';
import { hasNoRoute, noRouteWords, type ClosedLiftScope } from '../../state/lift-rule-waiting';
import { sameLevel } from '../../utils/levels';
import { durationWords } from '../Devices/devices-rows';
import type { RobotPresence } from '../Map/robot-presence';
import { cleanModeOfTask, taskTypeText } from '../Tasks/clean-modes';
import { phaseLabel } from '../Tasks/task-phase-label';
import { isReportingNow, locationText, needsLocalising } from './robot-lines';

export type RobotStatusClass =
  | 'working'
  | 'idle'
  | 'charging'
  | 'paused'
  | 'needs_help'
  | 'out_of_service'
  | 'not_reporting'
  | 'checking';

export interface RobotStatusCell {
  readonly cls: RobotStatusClass;
  readonly status: StatusKind;
  readonly label: string;
  readonly small: string | null;
  /** Why a Needs help robot needs it. */
  readonly help: RobotHelp | null;
}

export interface RobotHelp {
  readonly key: RobotCopyKey | 'battery_very_low';
  readonly band: Band;
  readonly headline: string;
  /** The first thing to do, in the attention list's words. */
  readonly firstStep: string | null;
}

/** The reasons that mean nobody can hear the robot, rather than that it
 *  needs a person: it reads Not reporting, and raises no card here. */
const NOT_REPORTING_KEYS: ReadonlySet<RobotCopyKey> = new Set<RobotCopyKey>([
  'state_channel_stale',
  'edge_offline',
  'not_reporting',
]);

const STATUS_WORDS: Readonly<
  Record<RobotStatusClass, { status: StatusKind; label: string; rank: number }>
> = {
  needs_help: { status: 'error', label: 'Needs help', rank: 0 },
  not_reporting: { status: 'offline', label: 'Not reporting', rank: 1 },
  paused: { status: 'warning', label: 'Paused', rank: 2 },
  working: { status: 'active', label: 'Working', rank: 3 },
  charging: { status: 'charging', label: 'Charging', rank: 4 },
  idle: { status: 'idle', label: 'Idle', rank: 5 },
  checking: { status: 'idle', label: 'Checking', rank: 6 },
  out_of_service: { status: 'warning', label: 'Out of service', rank: 7 },
};

/** Worst first, for the sort. */
export function statusRank(cls: RobotStatusClass): number {
  return STATUS_WORDS[cls].rank;
}

function helpFromRow(
  row: RobotCopyRow,
  arg: string | null,
  robot: string,
  level: string | null,
): RobotHelp {
  const context = { robot, arg, level };
  return {
    key: row.key,
    band: row.band,
    headline: row.headline(context),
    firstStep: row.steps(context)[0] ?? null,
  };
}

/** The worst thing the robot's own reports say, or null. A row kept from
 *  a site audience stays kept from it here. */
export function robotHelp(input: {
  readonly robot: Pick<RobotStateView, 'name' | 'status' | 'unavailableReason'>;
  readonly issues: readonly string[];
  readonly audience: Audience;
  readonly level: string | null;
}): RobotHelp | null {
  const { robot, issues, audience, level } = input;
  const found: RobotHelp[] = [];
  for (const category of issues) {
    const { row, arg } = robotCopyForCategory(category);
    if (row.opsOnly && audience !== 'ops') continue;
    found.push(helpFromRow(row, arg, robot.name, level));
  }
  const byReason = robotCopyForReason(robot.unavailableReason);
  if (byReason && !(byReason.opsOnly && audience !== 'ops')) {
    found.push(helpFromRow(byReason, null, robot.name, level));
  }
  if (found.length === 0 && robot.status === 'error') {
    found.push(helpFromRow(robotCopyRow('robot_error'), null, robot.name, level));
  }
  found.sort((a, b) => BAND_RANK[a.band] - BAND_RANK[b.band]);
  return found[0] ?? null;
}

export interface RobotStatusInput {
  readonly robot: Pick<RobotStateView, 'name' | 'status' | 'battery' | 'unavailableReason'>;
  readonly presence: RobotPresence | undefined;
  readonly clamped: boolean;
  readonly commission: RobotCommissionState;
  readonly issues: readonly string[];
  readonly task: Pick<TaskStateView, 'status' | 'type' | 'phases'> | null;
  readonly audience: Audience;
  /** Who took it out of service and when (`api/device_notes.py`), in words. */
  readonly outOfServiceWords?: string | null;
}

const cell = (cls: RobotStatusClass, small: string | null, help: RobotHelp | null = null): RobotStatusCell => ({
  cls,
  status: STATUS_WORDS[cls].status,
  label: STATUS_WORDS[cls].label,
  small,
  help,
});

/**
 * The Status column. Taken out of service first: it says why every other
 * word would be beside the point. Then whether anyone can hear the robot
 * at all, because a silent robot's last status is not its status now.
 */
export function robotStatusCell(input: RobotStatusInput): RobotStatusCell {
  const { robot, presence, clamped, commission, issues, task, audience } = input;
  if (commission === 'decommissioned') {
    return cell('out_of_service', input.outOfServiceWords || 'Taken out of service');
  }

  const level = presence?.pose?.source === 'live' ? presence.pose.map : null;
  const help = robotHelp({ robot, issues, audience, level });
  if (help && NOT_REPORTING_KEYS.has(help.key as RobotCopyKey)) {
    return cell('not_reporting', help.headline);
  }
  if (help) return cell('needs_help', help.headline, help);
  if (needsLocalising(robot, presence)) {
    return cell(
      'needs_help',
      'Lost its place on the map',
      helpFromRow(robotCopyRow('not_localized'), null, robot.name, level),
    );
  }
  if (!presence || presence.kind === 'lost' || robot.status === 'offline') {
    return cell('not_reporting', null);
  }
  if (presence.kind === 'checking') return cell('checking', null);

  const reporting = isReportingNow(presence, clamped);
  const battery = robot.battery;
  if (
    reporting &&
    robot.status !== 'charging' &&
    typeof battery === 'number' &&
    battery < BATTERY_VERY_LOW_PCT
  ) {
    return cell('needs_help', 'Battery very low', {
      key: 'battery_very_low',
      band: 'blocked',
      headline: 'Battery very low',
      firstStep: 'Open its panel to see what it can do.',
    });
  }
  if (task?.status === 'interrupted') return cell('paused', taskTypeWords(task));
  if (robot.status === 'charging') return cell('charging', 'At its charger');
  if (robot.status === 'active') {
    const riding = presence.kind === 'riding' && presence.liftName ? `in ${presence.liftName}` : null;
    const words = [task ? taskTypeWords(task) : null, riding].filter(Boolean).join(', ');
    return cell('working', words || null);
  }
  if (robot.status === 'idle') return cell('idle', null);
  return cell('needs_help', null);
}

function taskTypeWords(task: Pick<TaskStateView, 'type' | 'phases'>): string {
  return taskTypeText(task.type, cleanModeOfTask(task)).toLowerCase();
}

// ----------------------------------------------------------------------------
// Doing.
// ----------------------------------------------------------------------------

/** The job a robot is on: the one it names, else one assigned to it, running
 *  before waiting. */
export function robotTaskOf(
  robot: Pick<RobotStateView, 'name' | 'fleet' | 'taskId'>,
  tasks: readonly TaskStateView[],
): TaskStateView | null {
  const own = (t: TaskStateView) =>
    t.assignedRobot === robot.name && (!t.assignedFleet || t.assignedFleet === robot.fleet);
  return (
    (robot.taskId ? tasks.find((t) => t.id === robot.taskId) : undefined) ??
    tasks.find((t) => own(t) && (t.status === 'active' || t.status === 'interrupted')) ??
    tasks.find((t) => own(t) && t.status === 'queued') ??
    null
  );
}

/**
 * A map name, as a person would say it: "L17-Lobby" reads "Lobby" beside
 * its floor, "L04-MaintenancePoint-1" reads "Maintenance Point 1". The
 * names are the map's; only their punctuation goes.
 */
export function placeWords(name: string): string {
  return name
    .replace(/^L\d+[A-Z]*[-_]/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
}

export interface DoingInput {
  readonly task: Pick<
    TaskStateView,
    'status' | 'type' | 'phases' | 'estimatedRemainingSec' | 'noRouteSinceMs'
  > | null;
  readonly nextRun: Pick<ScheduleOccurrence, 'name' | 'start_ms'> | null;
  readonly clock: (epochMs: number) => string;
  /** The lift scopes closed at this site, for a task with no route. */
  readonly liftClosed?: readonly ClosedLiftScope[] | null;
}

/** "Vacuum · Lobby · 12 min left", or "Nothing · next run 13:30 Kitchen". */
export function doingWords({ task, nextRun, clock, liftClosed }: DoingInput): string {
  if (task) {
    const place = task.phases?.find((p) => typeof p.params?.place === 'string');
    const type = taskTypeText(task.type, cleanModeOfTask(task));
    const parts = [
      `${type.charAt(0).toUpperCase()}${type.slice(1)}`,
      place ? placeWords(phaseLabel(place)) : null,
    ];
    const noRoute = hasNoRoute(task) ? noRouteWords(liftClosed ?? null) : null;
    if (noRoute?.liftBlocked) parts.push(noRoute.short);
    else if (task.status === 'interrupted') parts.push('paused');
    else if (task.status === 'queued') parts.push('waiting to start');
    else if (typeof task.estimatedRemainingSec === 'number') {
      // Whole minutes: RMF's estimate is not good to the second.
      parts.push(`${durationWords(Math.ceil(task.estimatedRemainingSec / 60) * 60_000)} left`);
    }
    return parts.filter(Boolean).join(' · ');
  }
  return nextRun ? `Nothing · next run ${clock(nextRun.start_ms)} ${nextRun.name}` : 'Nothing';
}

/**
 * The next run pinned to this robot. Only pinned ones (Akash, 2026-11-02): a
 * run RMF will hand to whichever robot it picks would read as next on every
 * idle robot at once. A run already fired has a status, and is not next.
 */
export function nextRunFor(
  robot: Pick<RobotStateView, 'name' | 'fleet'>,
  occurrences: readonly ScheduleOccurrence[],
  nowMs: number,
): ScheduleOccurrence | null {
  return (
    occurrences
      .filter(
        (o) =>
          !o.status &&
          o.start_ms > nowMs &&
          o.robot === robot.name &&
          (!o.fleet || o.fleet === robot.fleet),
      )
      .sort((a, b) => a.start_ms - b.start_ms)[0] ?? null
  );
}

// ----------------------------------------------------------------------------
// Where.
// ----------------------------------------------------------------------------

/** How close a named place must be to be worth saying. In the robot map's
 *  own units, which the places share: nothing says they are metres. */
export const NEAR_PLACE_MAX_MAP_UNITS = 8;

/**
 * "L17, by Lobby" for a robot being heard on that floor; the presence line
 * otherwise ("In lift SL1", "No signal · last seen…"). Places on the same
 * map only, and only a close one: a robot in a long corridor is better
 * left on its floor than sent looking at the wrong end of it.
 */
export function whereWords(
  presence: RobotPresence | undefined,
  places: readonly PlaceRegistry[],
  timeZone: string | null,
  wallNowMs: number,
): string {
  const line = locationText(presence, timeZone, wallNowMs);
  const pose = presence?.pose;
  if (!pose || presence?.kind !== 'live' || pose.source !== 'live') return line;
  let best: { name: string; distance: number } | null = null;
  for (const place of places) {
    if (!sameLevel(place.level, pose.map)) continue;
    const distance = Math.hypot(place.x - pose.x, place.y - pose.y);
    if (distance <= NEAR_PLACE_MAX_MAP_UNITS && (!best || distance < best.distance)) {
      best = { name: place.name, distance };
    }
  }
  return best ? `${pose.map}, by ${placeWords(best.name)}` : line;
}

// ----------------------------------------------------------------------------
// Summary line.
// ----------------------------------------------------------------------------

export interface FleetSummaryRobot {
  readonly name: string;
  readonly cell: Pick<RobotStatusCell, 'cls'>;
  /** The battery, only while the robot is reporting. */
  readonly battery: number | null;
}

export interface FleetSummary {
  readonly headline: string;
  readonly facts: readonly SummaryFact[];
}

const AVAILABLE: ReadonlySet<RobotStatusClass> = new Set<RobotStatusClass>([
  'working',
  'idle',
  'charging',
  'paused',
]);

/** "3 of 5 robots available · 2 working · 1 needs help · Lowest battery…".
 *  A count of nothing is left out: "0 charging" says nothing. */
export function fleetSummary(robots: readonly FleetSummaryRobot[]): FleetSummary {
  const count = (cls: RobotStatusClass) => robots.filter((r) => r.cell.cls === cls).length;
  const available = robots.filter((r) => AVAILABLE.has(r.cell.cls)).length;
  const facts: SummaryFact[] = [];
  const add = (cls: RobotStatusClass, label: string, tone: SummaryFact['tone']) => {
    const n = count(cls);
    if (n > 0) facts.push({ id: cls, label: `${n} ${label}`, tone });
  };
  add('working', 'working', 'ok');
  add('charging', 'charging', 'ok');
  add('paused', 'paused', 'warn');
  add('needs_help', count('needs_help') === 1 ? 'needs help' : 'need help', 'bad');
  add('not_reporting', 'not reporting', 'warn');
  add('out_of_service', 'out of service', 'quiet');
  const lowest = robots
    .filter((r) => r.battery !== null && r.cell.cls !== 'out_of_service')
    .sort((a, b) => a.battery! - b.battery!)[0];
  if (lowest) {
    const pct = lowest.battery!;
    facts.push({
      id: 'lowest-battery',
      label: `Lowest battery ${lowest.name} · ${pct}%`,
      tone: pct < BATTERY_VERY_LOW_PCT ? 'bad' : pct < BATTERY_LOW_PCT ? 'warn' : 'quiet',
    });
  }
  const noun = robots.length === 1 ? 'robot' : 'robots';
  return { headline: `${available} of ${robots.length} ${noun} available`, facts };
}

// ----------------------------------------------------------------------------
// Needs attention.
// ----------------------------------------------------------------------------

const BAND_SEVERITY: Readonly<Record<Band, AttentionSeverity>> = {
  safety: 'critical',
  blocked: 'warning',
  upkeep: 'info',
};

export interface FleetAttention {
  readonly id: string;
  readonly severity: AttentionSeverity;
  readonly title: string;
  readonly detail: string | undefined;
  readonly robot: string;
}

/**
 * One card per robot in Needs help, safety first. The card says what is
 * wrong and the first step; the controls stay in the robot's panel with
 * their guards (Akash, 2026-11-02): an e-stopped robot in a lift must not
 * have its job cancelled, and a button here could not know that.
 */
export function fleetAttention(
  robots: readonly { readonly name: string; readonly cell: Pick<RobotStatusCell, 'help'> }[],
): FleetAttention[] {
  return robots
    .flatMap((r) => (r.cell.help ? [{ name: r.name, help: r.cell.help }] : []))
    .sort((a, b) => BAND_RANK[a.help.band] - BAND_RANK[b.help.band])
    .map(({ name, help }) => ({
      id: `robot-${name}`,
      severity: BAND_SEVERITY[help.band],
      title: `${name}: ${help.headline}`,
      detail: help.firstStep ?? undefined,
      robot: name,
    }));
}

/** "by R. Okafor, 09:10"; no name, the time alone; no note, null. */
export function outOfServiceWords(
  note: { readonly by: string | null; readonly at_ms: number } | undefined,
  clock: (ms: number) => string,
): string | null {
  if (!note) return null;
  return note.by ? `by ${note.by}, ${clock(note.at_ms)}` : `since ${clock(note.at_ms)}`;
}

/** "Auto charging off · R. Okafor · brush change". */
export function autoChargeWords(
  note: { readonly by: string | null; readonly reason: string | null } | undefined,
): string {
  return ['Auto charging off', note?.by, note?.reason].filter(Boolean).join(' · ');
}

/** The site's date, as the metrics read takes it. */
export function siteDateIso(atMs: number, timeZone: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone ?? undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(atMs);
}
